export type TourPage = '/integracoes' | '/ia' | '/ia/bulk-send' | '/' | '/leads-salvos';

export interface TourStep {
    id: string;
    page: TourPage;
    targetSelector: string;
    title: string;
    description: string;
    position: 'top' | 'bottom' | 'left' | 'right' | 'center';
    action?: {
        type: 'navigate' | 'click';
        target?: string;
    };

    // Obriga o tour a aguardar o elemento existir e estar visível no DOM
    waitForSelector?: boolean;

    // Propriedades do tour interativo
    requireAction?: boolean;          // Se true, usuário precisa interagir para avançar
    blockNext?: boolean;              // Se true, esconde botão "Próximo"
    preventDefaultAction?: boolean;   // Previne ação padrão do elemento (ex: abrir modal)

    // Alinhamento com documentação
    docStep?: number;                 // Correspondência com guia externo
}

export const tourSteps: TourStep[] = [

    // ================================
    // PASSO 0 — BOAS-VINDAS NO DASHBOARD
    // ================================
    {
        id: 'welcome',
        page: '/',
        targetSelector: 'body',
        title: 'tour.welcome.title',
        description: 'tour.welcome.description',
        position: 'center',
        docStep: 1,
    },

    // ==========================================
    // NAVEGAÇÃO PARA INTEGRAÇÕES PELO MENU
    // ==========================================
    {
        id: 'nav-to-integrations',
        page: '/',
        targetSelector: '[data-tour="nav-integrations"]',
        title: 'tour.nav_integrations.title',
        description: 'tour.nav_integrations.description',
        position: 'bottom',

        // Usuário precisa clicar
        requireAction: true,

        // Impede avanço manual
        blockNext: true,

        // Permite navegação real de página
        action: {
            type: 'navigate',
            target: '/integracoes',
        },

        docStep: 1,
    },

    // =====================================================
    // CARDS DE INTEGRAÇÃO - LAYOUT IDÊNTICO
    // =====================================================
    // Ambos aguardam DOM estabilizar e têm mesma configuração
    // para garantir apresentação visual consistente
    // =====================================================

    // ==========================
    // PASSO 1 — CARD DO WHATSAPP
    // ==========================
    {
        id: 'whatsapp-card',
        page: '/integracoes',
        targetSelector: '[data-tour="whatsapp-integration"]',
        title: 'tour.whatsapp.title',
        description: 'tour.whatsapp.description',
        position: 'bottom',

        // Aguarda DOM estabilizar (500ms máximo)
        waitForSelector: true,

        // Requer clique do usuário
        requireAction: true,

        // Não abre modal real
        preventDefaultAction: true,

        docStep: 1,
    },

    // ==========================
    // PASSO 2 — CARD DO SERPAPI
    // ==========================
    {
        id: 'serpapi-card',
        page: '/integracoes',
        targetSelector: '[data-tour="serpapi-integration"]',
        title: 'tour.serpapi.title',
        description: 'tour.serpapi.description',
        position: 'bottom',

        // Aguarda DOM estabilizar (500ms máximo)
        waitForSelector: true,

        // Requer clique do usuário
        requireAction: true,

        // Não abre modal real
        preventDefaultAction: true,

        docStep: 2,
    },

    // =====================================
    // NAVEGAÇÃO PARA A PÁGINA DE IA
    // =====================================
    {
        id: 'nav-to-ai',
        page: '/integracoes',
        targetSelector: '[data-tour="nav-ia"]',
        title: 'tour.nav_ia.title',
        description: 'tour.nav_ia.description',
        position: 'bottom',

        // Usuário deve clicar
        requireAction: true,

        // Bloqueia avanço manual
        blockNext: true,

        // Previne ações paralelas
        preventDefaultAction: true,

        action: {
            type: 'navigate',
            target: '/ia',
        },

        docStep: 3,
    },

    // ==========================
    // VISÃO GERAL DA IA
    // ==========================
    {
        id: 'ai-welcome',
        page: '/ia',
        targetSelector: '[data-tour="ai-page"]',
        title: 'tour.ai.title',
        description: 'tour.ai.description',
        position: 'center',
        docStep: 3,
    },

    // =====================================================
    // CARDS DE IA - LAYOUT IDÊNTICO
    // =====================================================
    // Ambos aguardam DOM e têm mesma configuração visual
    // =====================================================

    // =====================================
    // PASSO 1 — AGENTE DE DISPARO EM MASSA
    // =====================================
    {
        id: 'bulk-send-card',
        page: '/ia',
        targetSelector: '[data-tour="bulk-send-card"]',
        title: 'tour.bulk_send.title',
        description: 'tour.bulk_send.description',
        position: 'bottom',

        // Aguarda DOM estabilizar
        waitForSelector: true,

        // Requer clique do usuário
        requireAction: true,

        // Apenas visualização, não navega
        preventDefaultAction: true,

        docStep: 3,
    },

    // =====================================
    // PASSO 2 — AGENTE DE ATENDIMENTO WHATSAPP
    // =====================================
    {
        id: 'whatsapp-agent-card',
        page: '/ia',
        targetSelector: '[data-tour="whatsapp-agent-card"]',
        title: 'tour.whatsapp_agent.title',
        description: 'tour.whatsapp_agent.description',
        position: 'bottom',

        // Aguarda DOM estabilizar
        waitForSelector: true,

        // Requer clique do usuário
        requireAction: true,

        // Apenas visualização, não navega
        preventDefaultAction: true,

        docStep: 4,
    },

    // ==========================
    // =====================================================
    // DASHBOARD - SIMPLIFICADO
    // =====================================================
    // Elementos do Dashboard ainda não têm data-tour attributes.
    // Por isso pulamos direto para Leads Salvos.
    // TODO: Adicionar data-tour nos elementos do Dashboard
    // =====================================================
    // VOLTAR PARA DASHBOARD
    // ==========================
    {
        id: 'nav-to-dashboard',
        page: '/ia',
        targetSelector: '[data-tour="nav-dashboard"]',
        title: 'tour.nav_dashboard.title',
        description: 'tour.nav_dashboard.description',
        position: 'bottom',

        requireAction: true,
        blockNext: true,
        preventDefaultAction: true,

        action: {
            type: 'navigate',
            target: '/',
        },

        docStep: 5,
    },

    // ========================================
    // ELEMENTOS DO DASHBOARD ✅ ADICIONADOS
    // ========================================

    // ==========================
    // BARRA DE BUSCA
    // ==========================
    {
        id: 'dashboard-search',
        page: '/',
        targetSelector: '[data-tour="search-section"]',
        title: 'tour.dashboard_search.title',
        description: 'tour.dashboard_search.description',
        position: 'bottom',

        // Aguarda seção de busca carregar
        waitForSelector: true,

        docStep: 5,
    },

    // ==========================
    // SIDEBAR - ENVIO EM MASSA
    // ==========================
    {
        id: 'dashboard-sidebar',
        page: '/',
        targetSelector: '[data-tour="sidebar-bulk-send"]',
        title: 'tour.dashboard_sidebar.title',
        description: 'tour.dashboard_sidebar.description',
        position: 'left',

        // Aguarda sidebar carregar
        waitForSelector: true,

        docStep: 6,
    },

    // ==========================
    // NAVEGAR PARA LEADS SALVOS
    // ==========================
    {
        id: 'nav-to-leads',
        page: '/',
        targetSelector: '[data-tour="nav-leads-salvos"]',
        title: 'tour.nav_leads_salvos.title',
        description: 'tour.nav_leads_salvos.description',
        position: 'bottom',

        requireAction: true,
        blockNext: true,
        preventDefaultAction: true,

        action: {
            type: 'navigate',
            target: '/leads-salvos',
        },

        docStep: 8,
    },

    // ==========================
    // TABELA DE LEADS SALVOS
    // ==========================
    {
        id: 'saved-leads-table',
        page: '/leads-salvos',
        targetSelector: '[data-tour="saved-table"]',  // ✅ Corrigido para match o attribute real
        title: 'tour.saved_leads_table.title',
        description: 'tour.saved_leads_table.description',
        position: 'bottom',

        // Aguarda tabela carregar
        waitForSelector: true,

        docStep: 7,
    },

    // Tour finaliza aqui! 
    // O botão "Finalizar" no step saved-leads-table chamará completeTour()
    // automaticamente via handleNextAction no TourTooltip

];

export const getTourStepsForPage = (page: TourPage): TourStep[] => {
    // Filtra os passos da página atual
    const steps = tourSteps.filter(step => step.page === page);

    // Garante execução na ordem declarada no array (previne bug de ordem do DOM)
    return steps.sort((a, b) => getStepIndex(a.id) - getStepIndex(b.id));
};

export const getTotalSteps = (): number => {
    // Retorna total de passos do tour
    return tourSteps.length;
};

export const getStepIndex = (stepId: string): number => {
    // Retorna o índice do passo atual no fluxo completo
    return tourSteps.findIndex(step => step.id === stepId);
};
